import { useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { TicketDetail, TicketWorkEntry, TicketWorkLog } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen } from "@kundeninfo/ui";
import { Eingabe } from "@kundeninfo/ui/me-design";
import { QUARTER_MINUTES, hoursInput, parseHours, roundQuarter } from "./effort.js";
import { EFFORT_TEXT } from "./effortTexts.js";
import { berlinerZeitpunkt } from "./ticketFormat.js";
import { SchreibHinweis, useSchreibweg } from "./useSchreibweg.js";
import type { TicketsClient } from "./ticketsClient.js";

/**
 * DAS MODALPANEL „AUFWAND BUCHEN“ / „BUCHUNG LÖSCHEN“ (Spec Aufwand A20, P14): Buchen rechts angeschlagen, strikt, Kreuz,
 * am Telefon vollflächig (`Modal`); Löschen als Frage (siehe unten). „Speichern“ bzw. „Löschen“ ist die Bestätigung — keine zweite Rückfrage; „Abbrechen“ mit
 * Eingaben fragt nach (`darfAbbrechen`). Beide Wege antworten mit der Liste samt Summen (`TicketWorkLog`), die das Panel
 * über `onDone` an den Abschnitt reicht.
 *
 * Buchen: Datum (Vorgabe heute, Berliner Tag des Servers), Stunden in Viertelstunden, Bemerkung. Die Stunden runden beim
 * Verlassen auf Viertelstunden (P6: `me-eingabe` hat kein Blur-Prop, ein umschließendes `<span onBlur>` hört auf das
 * aufsteigende `focusout`); gesendet wird immer `roundQuarter` der gelesenen Minuten — ganze Zahlen, auch ohne Verlassen
 * (Review H2: 1,1 h wären sonst 66,000…01 min). Ein Ref sperrt den zweiten Klick im selben Takt (Review Focus 4), `laeuft`
 * kommt erst mit dem nächsten Zeichnen; solange es läuft, sind Felder und Ausgang gesperrt (Muster TicketVerlauf) — was
 * man dann tippte, ginge mit der Antwort still verloren.
 *
 * Löschen: eine Bestätigung ohne Eingabefelder, also klein, mittig und gestapelt (`Modal frage gefahr`, Hausregel
 * „Bestätigung vor Datenänderung“, Wächter bestaetigungGezeichnet.test.ts) — die Buchung lesend, „Löschen“ in
 * Gefahrfarbe. 404 heißt „schon gelöscht“: Das Panel holt die Liste und meldet `gone` — der Abschnitt zeigt den Hinweis.
 */

/** Wie WORK_NOTE_MAX_LENGTH der Domäne (A8): weiche Grenze mit Zähler, der Server prüft. */
const WORK_NOTE_MAX = 200;

export type WorkEntryPanelProps = {
  d: Pick<TicketDetail, "id" | "ticketNumber" | "heute">; client: TicketsClient;
  onDone: (log: TicketWorkLog, gone: boolean) => void; onAbbrechen: () => void;
} & ({ kind: "book" } | { kind: "delete"; entry: TicketWorkEntry });

export function WorkEntryPanel(p: WorkEntryPanelProps): ReactElement {
  return p.kind === "book" ? <Book {...p} /> : <Delete {...p} entry={p.entry} />;
}

type Gemeinsam = Omit<WorkEntryPanelProps, "kind" | "entry">;

/** Der Schreibweg beider Panels samt Doppelklick-Riegel. */
function useWeg(p: Gemeinsam) {
  const gone = useRef(false);
  const sendet = useRef(false);
  const weg = useSchreibweg<TicketWorkLog>((log) => p.onDone(log, gone.current), { felder: ["workDate", "minutes", "note"] });
  async function senden(arbeit: () => Promise<TicketWorkLog>): Promise<void> {
    if (sendet.current) return;
    sendet.current = true;
    try { await weg.schreiben(arbeit); } finally { sendet.current = false; }
  }
  return { weg, gone, senden };
}

function Book(p: Gemeinsam): ReactElement {
  const { weg, senden } = useWeg(p);
  const [datum, setDatum] = useState(p.d.heute);
  const [stunden, setStunden] = useState("");
  const [bemerkung, setBemerkung] = useState("");
  const [gerundet, setGerundet] = useState(false);
  const [befund, setBefund] = useState<string | undefined>(undefined);
  const m = parseHours(stunden);
  const minuten = typeof m === "number" ? roundQuarter(m) : null;
  const gesperrt = minuten === null || minuten < QUARTER_MINUTES || weg.laeuft;
  const stundenFehler = weg.feldFehler.minutes ?? befund;

  function runden(): void {
    if (m === undefined) { setBefund(EFFORT_TEXT.unreadable); return; }
    if (m === null) return;
    const r = roundQuarter(m);
    setStunden(hoursInput(r));
    setGerundet(r !== m);
    setBefund(r < QUARTER_MINUTES ? EFFORT_TEXT.minQuarter : undefined);
  }
  function speichern(): void {
    if (gesperrt || minuten === null) return;
    void senden(() => p.client.addTicketWorkEntry(p.d.id,
      { workDate: datum, minutes: minuten, note: bemerkung.trim() === "" ? null : bemerkung }));
  }
  async function abbrechen(): Promise<void> {
    if (weg.laeuft) return;
    if (await darfAbbrechen(stunden !== "" || bemerkung !== "" || datum !== p.d.heute)) p.onAbbrechen();
  }

  return (
    <Modal titel={EFFORT_TEXT.panelBook(p.d.ticketNumber)} onAbbrechen={() => { void abbrechen(); }} abbrechenGesperrt={weg.laeuft}>
      <Feld id="tw-datum" typ="date" label={EFFORT_TEXT.columns.date} wert={datum} bearbeitbar pflicht gesperrt={weg.laeuft}
        fehler={weg.feldFehler.workDate} onChange={(w) => { weg.feldRaeumen("workDate"); setDatum(w); }} />
      <span className="tw__stunden" onBlur={runden}>
        <Eingabe art="dezimal" einheit="h" id="tw-stunden" name="tw-stunden" beschriftung={EFFORT_TEXT.columns.hours}
          wert={stunden} pflicht hilfe={gerundet ? EFFORT_TEXT.rounded : EFFORT_TEXT.hoursHint}
          {...(weg.laeuft ? { gesperrt: true } : {})}
          {...(stundenFehler === undefined ? {} : { fehler: stundenFehler })}
          onGeaendert={(e) => { weg.feldRaeumen("minutes"); setBefund(undefined); setGerundet(false); setStunden(e.wert); }} />
      </span>
      <Feld id="tw-bemerkung" label={EFFORT_TEXT.columns.note} wert={bemerkung} bearbeitbar max={WORK_NOTE_MAX} gesperrt={weg.laeuft}
        fehler={weg.feldFehler.note} onChange={(w) => { weg.feldRaeumen("note"); setBemerkung(w); }} />
      <SchreibHinweis meldung={weg.meldung} />
      <ModalFuss speichernText="Speichern" onSpeichern={speichern} onAbbrechen={() => { void abbrechen(); }}
        speichernGesperrt={gesperrt} abbrechenGesperrt={weg.laeuft} />
    </Modal>
  );
}

function Delete(p: Gemeinsam & { entry: TicketWorkEntry }): ReactElement {
  const { weg, gone, senden } = useWeg(p);
  const e = p.entry;
  function loeschen(): void {
    void senden(async () => {
      try {
        return await p.client.deleteTicketWorkEntry(p.d.id, e.id);
      } catch (f) {
        if (!(f instanceof ApiError) || f.status !== 404) throw f;
        gone.current = true;
        return await p.client.listTicketWorkEntries(p.d.id);
      }
    });
  }
  return (
    <Modal titel={EFFORT_TEXT.panelDelete} frage gefahr onAbbrechen={p.onAbbrechen} abbrechenGesperrt={weg.laeuft}>
      <dl className="tw__lesend">
        <dt>{EFFORT_TEXT.columns.date}</dt><dd>{EFFORT_TEXT.day(e.workDate)}</dd>
        <dt>{EFFORT_TEXT.columns.hours}</dt><dd>{EFFORT_TEXT.hours(e.minutes)}</dd>
        <dt>{EFFORT_TEXT.columns.person}</dt><dd>{e.user.name}</dd>
        <dt>{EFFORT_TEXT.columns.note}</dt><dd className="tw__bemerkung">{e.note ?? EFFORT_TEXT.none}</dd>
        <dt>{EFFORT_TEXT.bookedAt}</dt><dd>{berlinerZeitpunkt(e.createdAt)}</dd>
      </dl>
      <SchreibHinweis meldung={weg.meldung} />
      <ModalFuss gefahr speichernText="Löschen" onSpeichern={loeschen} onAbbrechen={p.onAbbrechen}
        speichernGesperrt={weg.laeuft} abbrechenGesperrt={weg.laeuft} />
    </Modal>
  );
}
